/**
 * Attendance, as the Session model actually records it.
 *
 * ═══ WHY THIS FILE EXISTS ═══
 *
 * "Attendance" used to mean a row in `attendance`, written by a clock-in RPC.
 * `0098` retired the therapist door and `0121` the rider one, so that table has
 * had no writer since 2026-08-22 and is now closed history. Duty is a started
 * Secured Session and nothing else — TEND-008/009 for therapists, RIDER-005 for
 * riders ("There is no separate Go Online action").
 *
 * The surfaces that reported attendance were never moved across. This is the
 * shared derivation they need, kept pure so the states can be asserted in a
 * unit test rather than reached by changing the system clock — the same reason
 * `therapistDuty.ts` and `dutyPresence.ts` are pure.
 *
 * ═══ TWO AXES, AND CONFLATING THEM IS THE BUG THIS PREVENTS ═══
 *
 * `10 Therapist End/Therapist Session Daily Operations` files Late Start under
 * ATTENDANCE OUTCOME (Completed / Late Start / Early End / Incomplete /
 * No-Show), which is a DIFFERENT axis from the Session state and is *recorded,
 * never sat in*. The approved state machine has no Late and no Absent.
 *
 * So this returns both and never merges them:
 *
 *   state    where the Session is right now  — derived, the exceptions stored
 *   outcome  what the day is recorded as     — stored by the nightly settle
 *
 * ⚠ AND THE OUTCOME IS ONLY PREVIEWED ONCE THE WINDOW HAS CLOSED. See
 * `previewOutcome` below: previewing a running Session would mark every live
 * therapist `early_end`, because her last pin is necessarily older than a
 * midnight that has not arrived. While the Session runs, `runningLate` is the
 * honest signal and it is a prompt, not a record.
 *
 * ⚠ THIS IS A PREVIEW, NOT THE TRUTH — the same standing as the money-math
 * previews. `settle_therapist_sessions` (0157) and `end_rider_session` (0063)
 * write the stored column; a caller that HAS `outcome` must show that and never
 * this. `attendanceOf` enforces the precedence rather than trusting callers to.
 */

import {
  therapistSessionOutcome,
  type TherapistSessionOutcome,
} from "./therapist-session-outcome.ts";

/** CORE-04 and TEND-019 carry the identical five values. */
export type SessionAttendanceOutcome = TherapistSessionOutcome;

/**
 * The stored exception states, both roles.
 *
 * `0100`'s header is the reason there is no `available` here: Available and
 * PreStartWaiting are DERIVED from the clock so that nothing which decides
 * dispatchability depends on a job running on time. A stored value for them
 * would become a second source of truth a missed tick could leave stale.
 *
 * `extended` and `unable_to_continue` are the rider's alone
 * (`rider_session_state`, 0063); therapists have neither.
 *
 * ⚠ `extended` IS NOT FOLDED INTO `continuing_responsibility`, though they
 * describe the same shape of work. The rider's is a Session that ran past its
 * scheduled end under a chosen `max_extension_hours` allowance (RS-006); the
 * therapist's is a fixed period that ended at midnight while a booking was
 * still live (TEND-017). Merging them would put one role's allowance semantics
 * on the other's, and the two are settled by different jobs.
 */
export type SessionStoredState =
  | "paused"
  | "emergency_paused"
  | "continuing_responsibility"
  | "extended"
  | "session_done"
  | "unable_to_continue";

export type SessionAttendanceState =
  /** A finalized Off date. Not working, and not failing to. */
  | "off"
  /** Secured, but the cycle has not finalized — `start_*_session` would refuse it. */
  | "awaiting_finalization"
  /** Secured and finalized, never started. At midnight this becomes a No-Show. */
  | "not_started"
  /** Started before the scheduled start. Visible, deliberately NOT dispatchable. */
  | "pre_start_waiting"
  /** Started and inside the window — dispatch can allocate work. */
  | "available"
  | "paused"
  | "emergency_paused"
  /** Midnight passed while live work was still held (TEND-017). */
  | "continuing_responsibility"
  /** Rider only: running past the scheduled end on the chosen allowance (RS-006). */
  | "extended"
  | "session_done"
  | "unable_to_continue";

export interface SessionAttendanceInput {
  /** Therapist rule and rider rule differ on `early_end` — see `previewOutcome`. */
  role: "therapist" | "rider";
  /**
   * Did this person hold the date at all? False for a therapist Off date and
   * for a rider Session nobody secured.
   */
  secured: boolean;
  /**
   * Therapist cycles only: the securing is still a draft, so the date is not
   * yet hers to work. Riders secure directly and never pass this.
   */
  draft?: boolean;
  /** The stored exception column. `null` means "derive from the clock". */
  storedState: SessionStoredState | null;
  /** Scheduled start — 2:00 PM Manila for a therapist, the code window for a rider. */
  startsAt: string;
  /** The fixed end. Midnight for a therapist (TEND-016). */
  endsAt: string;
  /** When Start was tapped. `null` = never started. */
  startedAt: string | null;
  /** When the Session actually closed. Riders end explicitly; therapists do not. */
  endedAt: string | null;
  /** The STORED outcome. Present only once the session has been settled. */
  outcome: SessionAttendanceOutcome | null;
  /**
   * Therapists only, and optional. Her last location fix
   * (`staff_live_locations.captured_at`), which OQ-120 makes the `early_end`
   * signal. Omit it and the preview downgrades to the three-outcome answer
   * rather than inventing a fifth — see `therapist-session-outcome.ts`.
   */
  lastSeenAt?: string | null;
  /** Evaluation clock, injected so tests do not depend on the machine. */
  nowIso: string;
}

export interface SessionAttendance {
  state: SessionAttendanceState;
  /**
   * The RECORDED fact: she started, and it was after the scheduled start.
   * TEND-014 / RIDER-013, no grace period — `>` and never `>=`, so 2:00:00 PM
   * is on time and 2:00:01 PM is not. Kept identical to
   * `therapist_session_outcome_of` and `therapistDutyView`; three
   * implementations of one boundary is how they drift.
   */
  lateStart: boolean;
  /**
   * Not started, the scheduled start has passed, and the window is still open.
   * She is heading for a Late Start and can still avoid a No-Show.
   *
   * ⚠ DELIBERATELY NOT `lateStart`. Nothing is recorded yet, and if she never
   * starts at all the recorded outcome is No-Show, not Late Start. Merging the
   * two would report a fact the database does not hold.
   */
  runningLate: boolean;
  /**
   * The stored outcome, or — once the window has closed with no stored value —
   * the preview. `null` while the Session is still running, because an outcome
   * is a statement about a day that finished.
   */
  outcome: SessionAttendanceOutcome | null;
  /** True when the stored column supplied it; false when this file previewed it. */
  outcomeIsRecorded: boolean;
  /**
   * The duty half of dispatchability. Presence — a fresh location fix for a
   * therapist (TEND-027), the heartbeat for a rider (0049) — is the OTHER half
   * and is not visible from here, so this is never on its own a promise that
   * dispatch can reach someone. `dutyPresence.ts` joins the two.
   */
  onDuty: boolean;
}

/**
 * CORE-04's rider outcome, mirroring `rider_session_outcome_of` (0063) exactly.
 *
 * ⚠ THE RIDER RULE FOR `early_end` IS NOT THE THERAPIST RULE, and they must not
 * be shared. A rider ENDS a Session explicitly, so Early End is
 * `ended_at < ends_at` — a fact. A therapist has no End action at all
 * (TEND-016: midnight ends it), so hers is OQ-120's reading of TEND-027's
 * five-minute location window. One function for both would silently apply one
 * role's rule to the other.
 *
 * ⚠ LATE START WINS OVER EARLY END, and that is 0063's named choice rather than
 * a stated rule: the spec gives no precedence, Late Start is the earlier and
 * non-recoverable fault, and both facts go into the event meta so Luigi can
 * rule the other way without the evidence being lost. `incomplete` is therefore
 * never produced here — the same as in SQL.
 */
export function riderSessionOutcome(input: {
  startsAt: string;
  endsAt: string;
  startedAt: string | null;
  endedAt: string | null;
}): SessionAttendanceOutcome {
  if (input.startedAt === null) return "no_show";

  const started = Date.parse(input.startedAt);
  const starts = Date.parse(input.startsAt);
  // An unparseable timestamp must not silently become "completed" — that turns
  // a data problem into a clean attendance record.
  if (Number.isNaN(started) || Number.isNaN(starts)) return "no_show";

  // RIDER-013: no grace. Checked first, per the precedence note above.
  if (started > starts) return "late_start";

  const ended = input.endedAt === null ? Number.NaN : Date.parse(input.endedAt);
  const ends = Date.parse(input.endsAt);
  // RIDER-033: no grace on this side either. An unparseable or absent end
  // proves nothing, so it is not read as early.
  if (!Number.isNaN(ended) && !Number.isNaN(ends) && ended < ends) return "early_end";

  return "completed";
}

/**
 * The outcome this Session WOULD be recorded as, for a window that has closed
 * but not yet been settled.
 *
 * ⚠ NEVER CALLED FOR A RUNNING SESSION, and the guard is in `attendanceOf`
 * rather than here so there is one place to read it. The therapist rule
 * compares her last pin against `endsAt - 5 minutes`; for a Session whose
 * midnight is still hours away that is true of every pin she has ever sent, so
 * previewing early would mark the entire on-shift roster `early_end`.
 */
function previewOutcome(input: SessionAttendanceInput): SessionAttendanceOutcome {
  if (input.role === "rider") {
    return riderSessionOutcome({
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      startedAt: input.startedAt,
      endedAt: input.endedAt,
    });
  }
  return therapistSessionOutcome({
    startedAt: input.startedAt,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    // Passed through as-is: `undefined` (caller cannot see her location) and
    // `null` (we looked and there is no pin) mean different things, and
    // `therapistSessionOutcome` distinguishes them.
    lastSeenAt: input.lastSeenAt,
  });
}

/**
 * Where this Session stands, and what the day is recorded as.
 *
 * @param input a Session row normalized across the two roles by the caller.
 */
export function attendanceOf(input: SessionAttendanceInput): SessionAttendance {
  const now = Date.parse(input.nowIso);
  const starts = Date.parse(input.startsAt);
  const ends = Date.parse(input.endsAt);
  const started = input.startedAt === null ? null : Date.parse(input.startedAt);

  // The recorded lateness, decided before any state branch: it is true of a
  // settled No-Show's row as much as of a live one, and it never depends on
  // where the state machine is.
  const lateStart =
    started !== null && !Number.isNaN(started) && !Number.isNaN(starts) && started > starts;

  // ── the state axis ────────────────────────────────────────────────────────
  //
  // Ordered most-decisive first. A stored state is an explicit record and
  // outranks anything derived from the clock; not holding the date at all
  // outranks every question about starting it.
  const state = ((): SessionAttendanceState => {
    if (input.storedState !== null) return input.storedState;
    if (!input.secured) return "off";
    if (input.draft === true) return "awaiting_finalization";
    if (started === null || Number.isNaN(started)) return "not_started";
    // TEND-013 / REND-008: started before the scheduled start is PreStartWaiting
    // — visible, not dispatchable, and it flips on its own with nothing to tap.
    if (!Number.isNaN(starts) && now < starts) return "pre_start_waiting";
    return "available";
  })();

  // ── the outcome axis ──────────────────────────────────────────────────────

  const windowClosed = !Number.isNaN(ends) && now >= ends;
  const outcomeIsRecorded = input.outcome !== null;
  const outcome = outcomeIsRecorded
    ? input.outcome
    : windowClosed
      ? previewOutcome(input)
      : null;

  return {
    state,
    lateStart,
    // Only while she can still act on it: a window that has closed is a
    // recorded No-Show, not a person running late.
    runningLate:
      state === "not_started" && !Number.isNaN(starts) && now > starts && !windowClosed,
    outcome,
    outcomeIsRecorded,
    // Both continuing states count: someone finishing a responsibility past the
    // scheduled end is working, and showing them as off is how an ops screen
    // reports a therapist in a client's home as having gone home.
    onDuty:
      state === "available" ||
      state === "continuing_responsibility" ||
      state === "extended",
  };
}

/**
 * What a staff-facing or ops surface calls each state.
 *
 * ⚠ Deliberately says nothing about WHY. TEND-068 caps the therapist's own
 * History at the status and her recorded times — never the reason, the evidence
 * or the decision trace — and an ops label that explained itself here would be
 * the string that later gets reused on her screen.
 */
export function sessionStateLabel(state: SessionAttendanceState): string {
  switch (state) {
    case "off":
      return "Off";
    case "awaiting_finalization":
      return "Awaiting finalization";
    case "not_started":
      return "Not started";
    case "pre_start_waiting":
      return "Waiting to start";
    case "available":
      return "Available";
    case "paused":
      return "Paused";
    case "emergency_paused":
      return "Emergency pause";
    case "continuing_responsibility":
      return "Continuing responsibility";
    case "extended":
      return "Extended";
    case "session_done":
      return "Session done";
    case "unable_to_continue":
      return "Unable to continue";
  }
}
